/**
 * May this person change this session's ranking order, or move its cut line?
 * (S-33, owner ruling 2026-10-14; migration 891.)
 *
 * Both need the "Change ranking order" switch on (session defaults, inherited
 * workflow → cycle → track → session, default yes). Changing the order also
 * needs the "Change ranking order" permission (workspace admins and the cycle
 * owner hold it too); the session's facilitator may always place the cut line.
 *
 * The answer comes from the server (get_session_rank_change_rule) — the same
 * rule override_session_rank and set_session_cut_line enforce — so the screen
 * draws a control only when the server would accept it (build contract §2.8).
 */
import { useCallback, useEffect, useState } from 'react';
import { getTypedSupabaseClient } from '../typedSupabaseClient';

export type RankChangeSwitchSource = 'session' | 'track' | 'cycle' | 'workflow' | 'workspace' | 'default';

export interface SessionRankChangeRule {
  switchOn: boolean;
  switchSource: RankChangeSwitchSource;
  hasPermission: boolean;
  canChangeOrder: boolean;
  canMoveCutLine: boolean;
  /** S-5 (mig 973): the session's facilitator may break a tie in their own session. */
  canBreakTies: boolean;
  /** Names of the roles in this workspace that grant "Change ranking order". */
  heldBy: string[];
}

/** untyped until db:types after prod apply (mig 891) */
interface RuleRow {
  success?: boolean;
  switch_on?: boolean;
  switch_source?: string;
  has_permission?: boolean;
  can_change_order?: boolean;
  can_move_cut_line?: boolean;
  can_break_ties?: boolean;
  held_by?: unknown;
}

const SOURCES: RankChangeSwitchSource[] = ['session', 'track', 'cycle', 'workflow', 'workspace', 'default'];

export function parseRankChangeRule(row: unknown): SessionRankChangeRule | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as RuleRow;
  if (r.success === false) return null;
  const source = SOURCES.includes(r.switch_source as RankChangeSwitchSource)
    ? (r.switch_source as RankChangeSwitchSource)
    : 'default';
  return {
    switchOn: r.switch_on !== false,
    switchSource: source,
    hasPermission: r.has_permission === true,
    canChangeOrder: r.can_change_order === true,
    canMoveCutLine: r.can_move_cut_line === true,
    canBreakTies: r.can_break_ties === true,
    heldBy: Array.isArray(r.held_by) ? r.held_by.filter((x): x is string => typeof x === 'string') : [],
  };
}

const SOURCE_WORDS: Record<RankChangeSwitchSource, string> = {
  session: "this session's settings",
  track: "the track's session defaults",
  cycle: "the cycle's session defaults",
  workflow: "the workflow's session defaults",
  workspace: "the workspace's session defaults",
  default: 'the session defaults',
};

/** The plain line shown in place of a control the person may not use. */
export function rankChangeNeedsLine(rule: SessionRankChangeRule, act: 'order' | 'cut_line'): string {
  if (!rule.switchOn) {
    return `Changing the ranking order${act === 'cut_line' ? ', the cut line included,' : ''} is turned off in ${SOURCE_WORDS[rule.switchSource]}. Whoever can change those settings can turn it on.`;
  }
  const holders = ['workspace admins', 'the cycle owner', ...rule.heldBy.map((n) => `the ${n} role`)];
  const who = holders.length > 1 ? `${holders.slice(0, -1).join(', ')} and ${holders[holders.length - 1]}` : holders[0];
  return act === 'cut_line'
    ? `Moving the cut line needs the session's facilitator or the "Change ranking order" permission, held by ${who}.`
    : `Changing the ranking order needs the "Change ranking order" permission, held by ${who}.`;
}

export async function fetchSessionRankChangeRule(sessionId: string): Promise<SessionRankChangeRule | null> {
  // The RPC is new in 891; the generated types do not have it yet.
  // Cast the client before binding: binding the typed rpc first makes TypeScript
  // expand every database function's signature (TS2589).
  const client = getTypedSupabaseClient() as unknown as {
    rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  };
  const rpc = client.rpc.bind(client);
  const { data, error } = await rpc('get_session_rank_change_rule', { p_session_id: sessionId });
  if (error) return null;
  return parseRankChangeRule(data);
}

/** The rule for one session; `reload` after the switch or the session changes. */
export function useSessionRankChangeRule(sessionId: string | null | undefined, refreshKey?: unknown) {
  const [rule, setRule] = useState<SessionRankChangeRule | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    void fetchSessionRankChangeRule(sessionId).then((r) => {
      if (cancelled) return;
      setRule(r);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshKey, tick]);

  return { rule, loaded, reload };
}
